// Sorting rules shared by both keyword tables, so Video Creative and Static
// Creative order the same list identically.

interface SortableKeyword {
  label?: string | null;
  keyword: string;
  createdAt?: string | null;
  updatedAt?: string | null;
}

/** What the Name column actually shows — the label, falling back to the term. */
export function keywordDisplayName(kw: SortableKeyword): string {
  return (kw.label || kw.keyword || '').trim();
}

// Snowflake hands timestamps back as 'YYYY-MM-DD HH:mm:ss.SSS', which happens to
// sort correctly as a string — but only while that format holds. Parsing is
// explicit about the intent and survives an ISO string arriving from anywhere
// else. An unparseable or missing date sorts last rather than to 1970, so a row
// with no timestamp doesn't masquerade as the oldest.
function timeOf(value?: string | null): number {
  if (!value) return Number.NEGATIVE_INFINITY;
  const ms = new Date(value.includes('T') ? value : value.replace(' ', 'T')).getTime();
  return Number.isNaN(ms) ? Number.NEGATIVE_INFINITY : ms;
}

/**
 * Comparators for `useSortableTable`.
 *
 * `name` exists because sorting on the raw `keyword` field would order by
 * something the column no longer displays — a list sorted by a hidden value
 * looks broken.
 */
export const keywordComparators = {
  name: (a: SortableKeyword, b: SortableKeyword) =>
    keywordDisplayName(a).localeCompare(keywordDisplayName(b), undefined, { sensitivity: 'base' }),
  createdAt: (a: SortableKeyword, b: SortableKeyword) => timeOf(a.createdAt) - timeOf(b.createdAt),
  updatedAt: (a: SortableKeyword, b: SortableKeyword) => timeOf(a.updatedAt) - timeOf(b.updatedAt),
};

/** Compact date for a table cell — the time is noise at this density. */
export function formatKeywordDate(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value.includes('T') ? value : value.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}
